"use client";

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";

import { readInternalSource } from "@/lib/analytics/internal-source";
import { useAddToCart } from "@/ui/components/cart/use-add-to-cart";

/**
 * Status of the in-flight add-to-cart submit, shared with the submit button(s)
 * so they can render pending/success without a form action (we POST to a stable
 * route handler instead of binding to a rotating Server Action).
 */
interface AtcStatus {
	pending: boolean;
	showSuccess: boolean;
	quantity: number;
	setQuantity: (quantity: number) => void;
}

const AtcStatusContext = createContext<AtcStatus | null>(null);
type PdpAddSurface = "pdp_buybox" | "pdp_sticky";

/** Read add-to-cart submit status when inside an <AddToCartForm>. */
export function useAtcStatus(): AtcStatus | null {
	return useContext(AtcStatusContext);
}

interface AddToCartFormProps {
	channel: string;
	variantId: string;
	productSlug: string;
	productName: string;
	variantName: string;
	children: ReactNode;
	className?: string;
}

export function AddToCartForm({
	channel,
	variantId,
	productSlug,
	productName,
	variantName,
	children,
	className,
}: AddToCartFormProps) {
	// The whole click → transient drawer line → mutation → confirmation choreography
	// (including the failure toast with its retry) lives in the shared runner; this
	// form only owns its button state and the live form values.
	const addToCart = useAddToCart();
	const [pending, setPending] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);
	const [quantity, setQuantity] = useState(1);
	const formRef = useRef<HTMLFormElement>(null);
	const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lastSurfaceRef = useRef<PdpAddSurface>("pdp_buybox");
	const submitLockRef = useRef(false);

	const submit = useCallback(
		async (surface: PdpAddSurface = lastSurfaceRef.current) => {
			const form = formRef.current;
			if (!form || submitLockRef.current) return;
			submitLockRef.current = true;
			lastSurfaceRef.current = surface;

			const data = new FormData(form);
			const currentVariantId = String(data.get("variantId") ?? "").trim();
			if (!channel || !currentVariantId) {
				submitLockRef.current = false;
				return;
			}

			// Non-pack products render a QuantityPicker that writes a hidden
			// <input name="quantity"> (1/2/3); pack-variant products keep quantity at 1
			// (qty is the variant). Read it live so qty selection is honored. The route
			// + parseAddLineInput clamp it to 1..10.
			const currentQuantity = Number.parseInt(String(data.get("quantity") ?? "1"), 10) || 1;
			const currentVariantName = String(data.get("variantName") ?? variantName);

			// The add inherits whatever brought the visitor to this PDP (`search` from a
			// result link, `calculator` from a buy CTA, handed over on click for this
			// pathname; `?src=` on older links) so search/calculator attribution survives
			// all the way into `product_added_to_cart`. Read live: the handoff outlives
			// dose/pack switches (they only change the query), and a validated read keeps a
			// forged `src` out of analytics.
			const referred =
				typeof window !== "undefined"
					? readInternalSource(window.location.search, window.location.hash, window.location.pathname)
					: {};

			setPending(true);
			const added = await addToCart({
				channel,
				variantId: currentVariantId,
				quantity: currentQuantity,
				productSlug,
				productName,
				variantName: currentVariantName,
				internalSource: referred.internal_source ?? "pdp",
				// Each source owns its detail vocabulary: search submission id, calculator
				// peptide slug — and the product slug when the PDP is its own source.
				internalSourceDetail: referred.internal_source ? referred.internal_source_detail : productSlug,
				addSurface: surface,
			});

			if (added) {
				if (successTimerRef.current) clearTimeout(successTimerRef.current);
				setShowSuccess(true);
				successTimerRef.current = setTimeout(() => setShowSuccess(false), 1500);
			}

			submitLockRef.current = false;
			setPending(false);
		},
		[addToCart, channel, productSlug, productName, variantName],
	);

	return (
		<AtcStatusContext.Provider value={{ pending, showSuccess, quantity, setQuantity }}>
			<form
				ref={formRef}
				id="pdp-atc-form"
				className={className}
				onSubmit={(e) => {
					e.preventDefault();
					const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLElement | null;
					const surface = submitter?.dataset.addSurface === "pdp_sticky" ? "pdp_sticky" : "pdp_buybox";
					void submit(surface);
				}}
			>
				<input type="hidden" name="channel" value={channel} />
				<input type="hidden" name="variantId" value={variantId} />
				<input type="hidden" name="productSlug" value={productSlug} />
				<input type="hidden" name="productName" value={productName} />
				<input type="hidden" name="variantName" value={variantName} />
				{children}
			</form>
		</AtcStatusContext.Provider>
	);
}
